import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { fg } from '@atlaskit/platform-feature-flags';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';

import { setRemixThinkingComplete } from '../../editor-commands/set-remix-thinking-complete';
import type { MalleableUiPlugin, PluginConfiguration } from '../../malleableUiPluginType';

interface RemixContentProps {
	api?: ExtractInjectionAPI<MalleableUiPlugin>;
	config: PluginConfiguration;
}

export const RemixContent = ({ api, config }: RemixContentProps): React.JSX.Element | null => {
	const { createRemixContentComponent } = config;
	const isModalOpen = useSharedPluginStateWithSelector(
		api,
		['malleableUi'],
		(states) => states.malleableUiState?.isRemixModalOpen || false,
	);

	const isThinking = useSharedPluginStateWithSelector(
		api,
		['malleableUi'],
		(states) => states.malleableUiState?.isThinking || false,
	);

	const isComplete = useSharedPluginStateWithSelector(
		api,
		['malleableUi'],
		(states) => states.malleableUiState?.isComplete || false,
	);

	const remixModalMetadata = useSharedPluginStateWithSelector(
		api,
		['malleableUi'],
		(states) => states.malleableUiState?.remixModalMetadata,
	);

	const remixSource = useSharedPluginStateWithSelector(
		api,
		['malleableUi'],
		(states) => states.malleableUiState?.remixSource,
	);

	// Start Remix experience for floating toolbar entry point (can't use hooks in toolbar config).
	const experienceTracker = useAIMateExperienceTracker();
	const prevIsModalOpen = useRef(false);
	useEffect(() => {
		if (!prevIsModalOpen.current && isModalOpen && remixSource === 'floatingToolbar') {
			experienceTracker.start({
				name: ExperienceName.REMIX_START,
				id: ExperienceName.REMIX_START,
				attributes: {
					entryPoint: 'editor',
					remixSource: 'floatingToolbar',
					product: 'confluence',
				},
			});
		}
		prevIsModalOpen.current = isModalOpen;
	}, [isModalOpen, remixSource, experienceTracker]);
	const Component = useMemo(() => createRemixContentComponent(), [createRemixContentComponent]);

	const handleModalClose = useCallback(() => {
		api?.core.actions.execute(api?.malleableUi.commands.closeRemixModal());
	}, [api]);

	const onStartThinking = useCallback(
		(options?: { contentType?: string }) => {
			api?.aiExperience?.actions.toggleAiEntryPoints?.(true);
			api?.core.actions.execute(api?.malleableUi.commands.startRemixThinking(options));
		},
		[api],
	);

	const onStopThinking = useCallback(
		(options?: { reopenModal?: boolean }) => {
			api?.aiExperience?.actions.toggleAiEntryPoints?.(false);
			api?.core.actions.execute(api?.malleableUi.commands.stopRemixThinking(options));
		},
		[api],
	);

	const onSetThinkingComplete = useCallback(() => {
		api?.core.actions.execute(setRemixThinkingComplete());
	}, [api]);

	if (!api || !Component) {
		return null;
	}

	const selectedContext = api?.aiExperience?.actions.getAISelectionContext();
	if (!selectedContext) {
		return null;
	}

	return (
		<Component
			inEditorLoading={config.inEditorLoading}
			isModalOpen={isModalOpen}
			isThinking={isThinking}
			isComplete={fg('platform_editor_ai_remix_in_editor_loading') ? isComplete : undefined}
			handleModalClose={handleModalClose}
			remixSource={remixSource}
			selectedContext={selectedContext}
			editorApi={api}
			onSetThinkingComplete={
				fg('platform_editor_ai_remix_in_editor_loading') ? onSetThinkingComplete : undefined
			}
			onStartThinking={onStartThinking}
			onStopThinking={onStopThinking}
			remixModalMetadata={remixModalMetadata}
			hiddenOptions={config.hiddenOptions}
		/>
	);
};
